import React, {useEffect, useState} from "react"
import {
  Button,
  ButtonGroup,
  Link,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Typography
} from "@one-thd/sui-atomic-components";
import { isEmpty } from "lodash";
import {
  getOTPv2,
  updatePasswordWithOTP
} from "../../../../api/custAccountApi";
import { useCustomerAccount } from "../../AccountProvider";
import { useCommonContext } from "../../CommonProvider";
import { MESSAGE, MESSAGE_TYPE } from "../../../../constants/messages";
import analytics from "../../../../utils/analytics";
import {
  formatPhoneNumber2,
  getPrimaryPhone,
} from "../../../../utils";
import OtpOptions from "./OtpOptions";
import { FieldError, TextField } from "../../../common/fields";
import { useFormik } from "formik";
import { getErrorCode, handleApiError } from "../../../../utils/errors";
import {OtpLoadingView} from "../../../NewVerifyOtpModal/OtpModalViews";

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height
  };
}

export const PasswordEditOtpModal = ({email, newPassword, otpOptions, otpOptionsEncryptedPayload, closeModal, onSuccess, resetEditPasswordField}) => {

  const MODAL_VIEWS = {
    OTP_DISPATCH: "OTP_DISPATCH",
    OTP_VERIFICATION: "OTP_VERIFICATION",
  }

  const context = useCustomerAccount();
  const { addAlert } = useCommonContext();
  const { setStale, account } = context;
  const [ isLoading, setIsLoading ] = useState(false);
  const [ modalView, setModalView ] = useState(MODAL_VIEWS.OTP_DISPATCH);
  const [ otpDispatchErrors, setOtpDispatchErrors ] = useState([]);
  const [ otpDestination, setOtpDestination ] = useState("");
  const [ encryptedPayload, setEncryptedPayload ] = useState(otpOptionsEncryptedPayload);
  const [ selectedOtpChannel, setSelectedOtpChannel ] = useState(otpOptions?.includes("sms") ? "" : "email");
  const [isLandlineSMS, setIsLandlineSMS] = useState(false);
  const phoneNumber = getPrimaryPhone(account);

  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  const verifyOtpForm = useFormik({
    initialValues: {
      otp: "",
    },
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    validate: (values => {
      const errors = {};
      errors.otp = [];
      if(values.otp.length === 0) {
        errors.otp.push("Please enter your six digit code.");
      } else if(values.otp.length <= 5 && values.otp.length >= 1) {
        errors.otp.push("The verification code must be 6 digits");
      }
      return !isEmpty(errors.otp) && errors;
    }),

    onSubmit: (values, actions) => {
      setIsLoading(true);
      const {otp} = values;
      updatePasswordWithOTP(newPassword, email, encryptedPayload, otp, "PasswordEdit").then(({response, error}) => {
        setIsLoading(false);
        if (response) {
          actions.resetForm();
          setStale();
          analytics.postSettingsSubmissionEvent("security:password edit success");
          addAlert(MESSAGE_TYPE.SUCCESS, MESSAGE.PASSWORD_CHANGED);
          closeModal();
          onSuccess();
        } else {
          const errorCode = getErrorCode(error);
          const errorMessage = handleApiError(error, "update password with otp");
          if(errorCode === "IAM_ERR_018") {
            addAlert(MESSAGE_TYPE.DANGER, MESSAGE.PASSWORD_CONTAINS_EMAIL)
            resetEditPasswordField();
            closeModal();
          } else if (errorCode === "AUTH_ERR_125") {
            addAlert(MESSAGE_TYPE.DANGER, MESSAGE.OTP_ATTEMPTS_EXHAUSTED);
            closeModal();
          } else if (errorCode === "IAM_ERR_014") {
            addAlert(MESSAGE_TYPE.DANGER, MESSAGE.PASSWORD_REPEATED);
            resetEditPasswordField();
            closeModal();
          } else {
            actions.setFieldError("otp", [errorMessage])
          }
        }
      });
    },
  });

  const closeModalAndResetForm = () => {
    closeModal();
    verifyOtpForm.resetForm();
  }

  const dispatchOTP = (otpChannel) => {
    setIsLoading(true);
    getOTPv2(email, otpOptionsEncryptedPayload, otpChannel).then(({response, error}) => {
      setIsLoading(false);
      if (response) {
        if (response.data?.isLandlineSMS && otpChannel === "sms") {
          setIsLandlineSMS(true);
          setSelectedOtpChannel("");
          return;
        }
        setEncryptedPayload(response.data?.encryptedPayload);
        setModalView(MODAL_VIEWS.OTP_VERIFICATION);
        setOtpDestination(otpChannel === "email" ? email : formatPhoneNumber2(phoneNumber));
        setOtpDispatchErrors([]);
      } else {
        const errorCode = getErrorCode(error);
        const errorMessage = handleApiError(error, "get otp for password update");
        if (errorCode === "AUTH_ERR_125") {
          addAlert(MESSAGE_TYPE.DANGER, MESSAGE.OTP_ATTEMPTS_EXHAUSTED);
          closeModal();
        } else if (errorCode === "CCA_ERR_002") {
          setIsLandlineSMS(true);
          setSelectedOtpChannel("");
        }
        else {
          const newEncryptedPayload = error?.response?.headers?.payload;
          if (newEncryptedPayload) {
            setEncryptedPayload(newEncryptedPayload);
          }
          setOtpDispatchErrors( [errorMessage]);
        }
      }
    });
  }

  const OTPDispatchOptionsBody = () => {
    const infoText = isLandlineSMS ?  MESSAGE.SMS_LANDLINE : "Before saving your password, we need to confirm your identity by sending a one-time verification code.";
    return (
      <div className="password-otp-options">
        <OtpOptions {...{closeModal, otpDispatchErrors, email, phoneNumber, infoText, selectedOtpChannel, setSelectedOtpChannel, isLandlineSMS}}/>
      </div>
    )
  }
  const OTPEmailDispatchBody = () => (
    <>
      <div className="u--paddingNormal-bottom">
        <Typography variant="body-base">
          Before saving your password, we need to confirm your identity by sending a one-time verification code to your email address:
        </Typography>
      </div>
      <Typography variant="body-base" weight="bold">
        {email}
      </Typography>
    </>
  );
  const OTPDispatchBody = otpOptions?.includes("sms") ? OTPDispatchOptionsBody : OTPEmailDispatchBody;
  const OTPDispatchFooter = () => (
    <ButtonGroup fullWidth responsive>
      <Button variant='primary' onClick={() => dispatchOTP(selectedOtpChannel)} disabled={selectedOtpChannel === ""}>
        Send Code
      </Button>
      <Button onClick={closeModal}>
        Cancel
      </Button>
    </ButtonGroup>
  );

  const onOtpChange = (e) => {
    const otp = e.target.value.trim().replace(/[^\d]/,"");
    verifyOtpForm.setFieldValue("otp", otp, true);
  }

  const VerifyOtpTextBox = () => <>
    <TextField maxLength={6}
               autoFocus // eslint-disable-line
               className="no-padding-sides u--paddingNormal-vertical"
               id="verifyOTP_EmailEdit"
               name="otp"
               type="OTP"
               label="Enter Verification Code"
               valid={!(verifyOtpForm.touched.otp && !isEmpty(verifyOtpForm.errors.otp))}
               onChange={onOtpChange}
               onBlur={verifyOtpForm.handleBlur}
               value={verifyOtpForm.values.otp}>
      {verifyOtpForm.touched.otp && !isEmpty(verifyOtpForm.errors.otp) &&
        <FieldError name="otp" errors={verifyOtpForm.errors.otp}/>}
    </TextField>
  </>;

  const otpChannelToAction = {
    "sms": "Text me",
    "phone": "Call me with",
    "email": "Email me",
  }
  const RetryLinks = () => ["sms", "phone", "email"].map((otpChannel) => {
    const onClickOption = () => {
      setSelectedOtpChannel(otpChannel);
      verifyOtpForm.resetForm();
      dispatchOTP(otpChannel);
    }
    return (
      <p key={otpChannel}>
        <button type="button"
                onClick={onClickOption}>
          <Link>{`${otpChannelToAction[otpChannel]} a${otpChannel === selectedOtpChannel ? " new" : ""} code`}</Link>
        </button>
      </p>
    );
  });

  const OTPAlternateOptions = () => (
    <div>
      <Typography>Having issues?</Typography>
      <Typography>Here are some options.</Typography>
      <div className="password-otp-retry-links">
        <RetryLinks/>
      </div>
    </div>
  );

  const onResendEmailOTP = () => {
    verifyOtpForm.resetForm();
    dispatchOTP("email");
  }
  const OTPEmailNewCodeOption = () => (
    <>
      <Typography>
        Didn&apos;t receive the code?
      </Typography>
      <Link onClick={onResendEmailOTP}>Email me a new code</Link>
    </>
  );

  const OTPVerificationBody = () => (
    <div>
      <Typography variant="body-base">
        We sent a verification code to
      </Typography>
      <Typography variant="body-base" weight="bold">
        {otpDestination}
      </Typography>
      <VerifyOtpTextBox/>
      {!!otpDispatchErrors.length &&
        <div className="otp-options-view-error col__12-12 no-padding-sides form-input--error">
          <FieldError name="otp" errors={otpDispatchErrors}/>
        </div>
      }
      {otpOptions.includes("sms") ?
        <OTPAlternateOptions/> :
        <OTPEmailNewCodeOption/>
      }
    </div>
  );
  const OTPVerificationFooter = () => (
    <ButtonGroup fullWidth responsive>
      <Button type="submit" variant='primary' onClick={verifyOtpForm.handleSubmit} disabled={!verifyOtpForm.isValid}>
        Submit
      </Button>
      <Button onClick={closeModalAndResetForm}>
        Cancel
      </Button>
    </ButtonGroup>
  )

  let modalBody, modalFooter;
  if (modalView === MODAL_VIEWS.OTP_DISPATCH) {
    modalBody = <OTPDispatchBody />;
    modalFooter = <OTPDispatchFooter />;
  } else {
    modalBody = <OTPVerificationBody />;
    modalFooter = <OTPVerificationFooter />;
  }

  const size = (windowDimensions.width < 1400) ? "md" : "sm";

  return (
    <Modal
      open
      onClose={closeModalAndResetForm}
      size={size}
    >
      <ModalHeader onClose={closeModalAndResetForm}>Verify Your Identity</ModalHeader>
      { isLoading ? (
        <div className="otp-loading-container">
          <OtpLoadingView />
        </div>
      ) : (
        <>
          <ModalBody>
            {modalBody}
          </ModalBody>
          <ModalFooter position='center'>
            { modalFooter }
          </ModalFooter>
        </>
      )}
    </Modal>
  )
}